import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { useCallback, useMemo } from 'react';
import { DataGrid, DataGridSortModel } from '@repo/components';
import GenericDataPageHeader from 'src/components/GenericDataPageHeader';
import { Site } from '@repo/shared/domain';
import { constructQuery, sortToString, stringToSort } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { createSite, getSites } from 'src/api/admin/sites';

interface SitesSearch {
	sort?: string;
	page?: number;
}

export const Route = createFileRoute('/_admin/system/sites/')({
	validateSearch: (search: Record<string, unknown>): SitesSearch => ({
		sort: search.sort as string | undefined,
		page: search.page as number | undefined,
	}),
	loaderDeps: ({ search: { sort, page } }) => ({ sort, page }),
	loader: async ({ deps }) => {
		const query = constructQuery({
			sort: deps.sort,
			page: deps.page,
			noPagination: 'false',
		});
		const sites = await getSites(query);
		return { sites };
	},
	component: SitesPage,
});

const columns = [
	{
		field: 'name',
		headerName: 'Name',
		flex: 2,
	},
	{
		field: 'timezone',
		headerName: 'Timezone',
		flex: 2,
		sortable: false,
	},
	{
		field: 'emergencyMode',
		headerName: 'Emergency Mode',
		flex: 1,
		renderCell: (cell: any) => (cell.value ? <CheckIcon /> : ''),
	},
];

function SitesPage() {
	const { sort, page } = Route.useSearch();
	const { sites: initialSites } = Route.useLoaderData();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const updateQueryParams = useUpdateQueryParams();

	const query = constructQuery({
		sort,
		page,
		noPagination: 'false',
	});

	const { data: sites, refetch: revalidateSites } = useQuery({
		queryKey: ['system-sites', query],
		queryFn: () => getSites(query),
		initialData: initialSites,
	});

	useBreadcrumbLevel(0, 'System');
	useBreadcrumbLevel(1, 'Sites');

	const handleRowClick = ({ row }: { row: Site }) => {
		navigate({ to: '/system/sites/$id', params: { id: row.id } });
	};

	const handleAddSite = useCallback(async () => {
		const response = await createSite();

		if (response.body?.id) {
			navigate({ to: '/system/sites/$id', params: { id: response.body.id } });
		}
	}, [navigate]);

	const handleSort = ([sortModel]: DataGridSortModel) => {
		updateQueryParams({ sort: sortModel ? sortToString(sortModel) : undefined });
	};

	const actions = useMemo(() => {
		return [
			<Button onClick={handleAddSite} startIcon={<AddIcon />} variant="outlined" key="addSite">
				Add Site
			</Button>,
		];
	}, [handleAddSite]);

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page });
	};

	return (
		<>
			<GenericDataPageHeader actions={actions} />
			<div className="p-4 pt-0">
				<Paper variant="outlined" sx={{ marginTop: 2 }}>
					<DataGrid
						data={sites.data}
						columns={columns}
						paginationMode="server"
						sortingMode="server"
						sortingOrder={['asc', 'desc']}
						pagination
						onRowClick={handleRowClick}
						onReload={revalidateSites}
						pageSize={sites.pageSize}
						pageSizeOptions={[sites.pageSize]}
						onPaginationModelChange={handlePaginationModelChange}
						sortModel={[stringToSort(sort || 'name:asc')]}
						page={sites.page}
						rowCount={sites.count}
						onSortModelChange={handleSort}
					/>
				</Paper>
			</div>
		</>
	);
}
